import { RefObject, useRef } from 'react';
import DataGrid, { Column, Selection } from 'devextreme-react/data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { onTypeRender, onDateRender, onPercentRender } from '../functions/functions';
import { IInspection } from '../interfaces/interfaces';
import { onFileRender } from './components/OnFileRender';
import { onStatusRender } from './components/OnStatusRender';
import { useInspectionsModalStore } from '../store/useInspectionsModalStore';
import { useInspectionsStateQuery } from '../queries/inspectionsQueries';

interface InspectionsGridProps {
    scrollableContentRef: RefObject<HTMLDivElement | null>;
    isOrderNewDisabled: boolean;
    onOrderNewInspection: () => void;
}

const InspectionsGrid = ({ scrollableContentRef, isOrderNewDisabled, onOrderNewInspection }: InspectionsGridProps) => {
    const { currentInspection, isNewInspection, setCurrentInspection, setIsNewInspection } = useInspectionsModalStore();
    const { data: inspectionsData } = useInspectionsStateQuery();

    const inspectionsHeaderRef = useRef<HTMLDivElement>(null);
    const gridRowDetailsMessageRef = useRef<HTMLDivElement>(null);

    const onInspectionSelect = (id: number) => {
        const insp = inspectionsData?.inspections.find((i) => i.id === id);
        if (!insp) return;
        setIsNewInspection(false);
        setCurrentInspection(insp);
    };

    const onDrawIdRender = (e: { value: unknown }): number | null => {
        const draw = inspectionsData?.draws.find((d) => d.id === e.value);
        return draw?.number ?? null;
    };

    const calculateInspectionGridHeight = (): string => {
        if (currentInspection || isNewInspection) {
            return '150px';
        }
        if (scrollableContentRef.current) {
            const inspectionsGridOffset = 53;
            return (
                scrollableContentRef.current.clientHeight -
                (inspectionsGridOffset +
                    (inspectionsHeaderRef.current?.clientHeight ?? 0) +
                    (gridRowDetailsMessageRef.current?.clientHeight ?? 0)) +
                'px'
            );
        }
        return '150px';
    };

    const styles = {
        formColumn: { paddingBottom: '3px' },
        formFirstRow: { marginBottom: '0' },
        formRow: { marginBottom: '10' },
    };

    return (
        <>
            <div
                className='row'
                style={styles.formFirstRow}
                ref={inspectionsHeaderRef}
            >
                <div
                    className='columns small-12 large-12'
                    style={styles.formColumn}
                >
                    <h2 className='decorated'>Ordered Inspections</h2>
                </div>
            </div>
            <div
                className='row'
                style={{ marginBottom: '-20px' }}
                ref={gridRowDetailsMessageRef}
            >
                <div
                    className='columns small-10 large-10'
                    style={{ paddingTop: '5px' }}
                >
                    Click on a row to view details...
                </div>
                <div className='columns small-2 large-2'>
                    <button
                        className='button hollow'
                        type='button'
                        title='Order a new Inspection'
                        onClick={onOrderNewInspection}
                        disabled={isOrderNewDisabled}
                        style={{ padding: '6px' }}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        &nbsp;Order New
                    </button>
                </div>
            </div>
            <div
                className='row'
                style={styles.formRow}
            >
                <div
                    className='columns small-12 large-12'
                    style={styles.formColumn}
                >
                    <DataGrid
                        id='inspectionsGrid'
                        keyExpr='id'
                        dataSource={inspectionsData?.inspections}
                        showBorders={true}
                        rowAlternationEnabled={true}
                        remoteOperations={false}
                        height={calculateInspectionGridHeight}
                        noDataText='No inspections have been ordered'
                        onCellClick={(e) => onInspectionSelect((e.data as IInspection).id)}
                    >
                        <Selection mode='single' />
                        <Column
                            dataField='inspectionTypeId'
                            caption='Type'
                            cellRender={onTypeRender}
                            width={100}
                            alignment='left'
                        />
                        <Column
                            dataField='drawId'
                            caption='Draw #'
                            cellRender={onDrawIdRender}
                            width={60}
                            alignment='center'
                        />
                        <Column
                            dataField='orderedDate'
                            caption='Ordered'
                            cellRender={onDateRender}
                            width={150}
                        />
                        <Column
                            dataField='refreshedDate'
                            caption='Last Update'
                            cellRender={onDateRender}
                            width={150}
                        />
                        <Column
                            dataField='statusId'
                            caption='Status'
                            cellRender={onStatusRender}
                            alignment='left'
                        />
                        <Column
                            dataField='percentComplete'
                            caption='Percent'
                            width={70}
                            cellRender={onPercentRender}
                            alignment='right'
                        />
                        <Column
                            dataField='reportUrl'
                            caption='File'
                            cellRender={onFileRender}
                            width={60}
                            alignment='center'
                        />
                    </DataGrid>
                </div>
            </div>
        </>
    );
};

export default InspectionsGrid;
